import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Bell, Calendar, Music, ExternalLink, Check, Loader2, Gift, Play, CreditCard } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { ReleaseCountdown } from '../components/ReleaseCountdown';
import { prepareCheckoutWindow, completeCheckoutRedirect, closeCheckoutWindow, isStandalonePWA } from '../utils/pwaCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

const getDaysUntil = (dateStr) => {
  const release = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function PresavesPage() {
  const { user, isAuthenticated } = useAuth();
  const { playPreview } = usePlayer();
  const navigate = useNavigate();
  const [presaves, setPresaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signupModal, setSignupModal] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState({});
  const [preordered, setPreordered] = useState({});
  const [preordering, setPreordering] = useState({});

  useEffect(() => {
    fetchPresaves();
    if (user) {
      setEmail(user.email || '');
      setName(user.name || '');
    }
  }, [user]);

  // Check paid pre-order status for any release that offers one
  useEffect(() => {
    if (!isAuthenticated || presaves.length === 0) return;
    presaves.filter(p => p.preorder_enabled).forEach(p => {
      axios.get(`${API_URL}/presaves/${p.id}/check-preorder`)
        .then(res => {
          if (res.data?.has_preordered) {
            setPreordered(prev => ({ ...prev, [p.id]: true }));
          }
        })
        .catch(() => {});
    });
  }, [isAuthenticated, presaves.length]);

  const fetchPresaves = async () => {
    try {
      const res = await axios.get(`${API_URL}/presaves`);
      setPresaves(res.data);
    } catch (error) {
      logger.error('Failed to fetch presaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreorder = async (presave) => {
    if (!isAuthenticated) {
      toast.error('Please log in to pre-order');
      navigate('/login');
      return;
    }
    setPreordering(prev => ({ ...prev, [presave.id]: true }));
    const pwaWindow = prepareCheckoutWindow();
    try {
      const res = await axios.post(`${API_URL}/presaves/${presave.id}/preorder`, {
        origin_url: window.location.origin,
      });
      if (isStandalonePWA()) {
        toast.info('Opening secure checkout in your browser. Come back here when done.');
      }
      completeCheckoutRedirect(pwaWindow, res.data.url);
    } catch (error) {
      closeCheckoutWindow(pwaWindow);
      toast.error(error.response?.data?.detail || 'Failed to start pre-order');
      setPreordering(prev => ({ ...prev, [presave.id]: false }));
    }
  };

  const handleSignup = async (presaveId) => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/presaves/${presaveId}/signup`, {
        presave_id: presaveId,
        email,
        name
      });
      toast.success('You\'re on the list! We\'ll notify you when it drops.');
      setSignedUp(prev => ({ ...prev, [presaveId]: true }));
      setSignupModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  const nextPresave = [...presaves]
    .filter(p => new Date(p.release_date) > new Date())
    .sort((a, b) => new Date(a.release_date) - new Date(b.release_date))[0];

  return (
    <div className="main-content pt-24" data-testid="presaves-page">
      <div className="container py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Upcoming Releases</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Pre-save upcoming music and be the first to know when it drops!
          </p>
        </div>

        {/* Big Countdown Banner for next drop */}
        {nextPresave && (
          <div className="mb-6" data-testid="presave-countdown-banner">
            <ReleaseCountdown
              releaseDate={nextPresave.release_date}
              title={nextPresave.title}
              coverUrl={nextPresave.cover_url}
              variant="hero"
            />
            {(nextPresave.spotify_url || nextPresave.apple_music_url) && (
              <div className="flex gap-3 mt-4 max-w-md mx-auto" data-testid="presave-banner-streaming-links">
                {nextPresave.spotify_url && (
                  <a
                    href={nextPresave.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-3 rounded-xl bg-[#1DB954]/15 text-[#1DB954] font-medium flex items-center justify-center gap-2 hover:bg-[#1DB954]/25 transition-colors"
                    data-testid="presave-banner-spotify-link"
                  >
                    Pre-save on Spotify <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {nextPresave.apple_music_url && (
                  <a
                    href={nextPresave.apple_music_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-3 rounded-xl bg-[#FA243C]/15 text-[#FA243C] font-medium flex items-center justify-center gap-2 hover:bg-[#FA243C]/25 transition-colors"
                    data-testid="presave-banner-apple-link"
                  >
                    Pre-save on Apple Music <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Presaves Grid */}
        {presaves.length === 0 ? (
          <div className="empty-state glass-card py-16 max-w-md mx-auto">
            <Music className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">No upcoming releases</h3>
            <p className="text-gray-400">Check back soon for new music announcements!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {presaves.map(presave => {
              const daysUntil = getDaysUntil(presave.release_date);
              const isSignedUp = signedUp[presave.id];
              
              return (
                <div key={presave.id} className="glass-card overflow-hidden group" data-testid={`presave-${presave.id}`}>
                  {/* Cover Image */}
                  <div className="relative aspect-square overflow-hidden">
                    <img 
                      src={presave.cover_url} 
                      alt={presave.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    
                    {/* Countdown Badge */}
                    <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-sm font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-pink-400" />
                      {daysUntil > 0 ? `${daysUntil} days` : 'Dropping Soon!'}
                    </div>
                    
                    {/* Title Overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold mb-1">{presave.title}</h3>
                      <p className="text-gray-300 text-sm">{presave.artist}</p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    {presave.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{presave.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm text-gray-400">
                        <span className="text-pink-400 font-medium">Release:</span> {formatDate(presave.release_date)}
                      </div>
                      {presave.signup_count > 0 && (
                        <div className="text-sm text-gray-500">
                          {presave.signup_count} pre-saved
                        </div>
                      )}
                    </div>
                    
                    {/* Action Button */}
                    {preordered[presave.id] ? (
                      <div className="space-y-2">
                        <button
                          disabled
                          className="w-full py-3 rounded-xl bg-green-500/20 text-green-400 font-medium flex items-center justify-center gap-2"
                        >
                          <Check className="w-5 h-5" /> Pre-ordered!
                        </button>
                        {presave.bonus_content_url && (
                          <Link
                            to={`/bonus/${presave.id}`}
                            className="w-full py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-300 text-sm font-medium flex items-center justify-center gap-2 hover:from-purple-500/30 hover:to-cyan-500/30 transition-colors"
                          >
                            <Gift className="w-4 h-4" /> View Bonus
                          </Link>
                        )}
                      </div>
                    ) : presave.preorder_enabled ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => handlePreorder(presave)}
                          disabled={preordering[presave.id]}
                          className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid={`preorder-btn-${presave.id}`}
                        >
                          {preordering[presave.id] ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <CreditCard className="w-5 h-5" />
                          )}
                          Pre-order for ${(presave.preorder_price ?? 1).toFixed(2)}
                        </button>
                        {presave.bonus_content_url && (
                          <p className="text-xs text-center text-purple-300/70">
                            <Gift className="w-3 h-3 inline mr-1" />
                            Includes exclusive bonus content!
                          </p>
                        )}
                        {!isSignedUp && (
                          <button
                            onClick={() => setSignupModal(presave)}
                            className="w-full text-xs text-gray-400 hover:text-gray-200 py-1"
                            data-testid={`presave-free-btn-${presave.id}`}
                          >
                            or just get notified for free
                          </button>
                        )}
                      </div>
                    ) : isSignedUp ? (
                      <button
                        disabled
                        className="w-full py-3 rounded-xl bg-green-500/20 text-green-400 font-medium flex items-center justify-center gap-2"
                      >
                        <Check className="w-5 h-5" /> Pre-saved!
                      </button>
                    ) : (
                      <button
                        onClick={() => setSignupModal(presave)}
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                        data-testid={`presave-btn-${presave.id}`}
                      >
                        <Bell className="w-5 h-5" /> Pre-save Now
                      </button>
                    )}
                    
                    {/* Streaming Links (if available) */}
                    {(presave.spotify_url || presave.apple_music_url || presave.audio_url) && (
                      <div className="flex gap-2 mt-3">
                        {presave.audio_url && (
                          <button
                            onClick={() => playPreview({ ...presave, audio_url: presave.audio_url }, 30)}
                            className="flex-1 py-2 rounded-lg bg-purple-500/20 text-purple-300 text-sm font-medium flex items-center justify-center gap-1 hover:bg-purple-500/30 transition-colors"
                            data-testid={`presave-preview-${presave.id}`}
                          >
                            <Play className="w-3 h-3" fill="currentColor" /> Preview 30s
                          </button>
                        )}
                        {presave.spotify_url && (
                          <a 
                            href={presave.spotify_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 py-2 rounded-lg bg-[#1DB954]/20 text-[#1DB954] text-sm font-medium flex items-center justify-center gap-1 hover:bg-[#1DB954]/30"
                          >
                            Spotify <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {presave.apple_music_url && (
                          <a 
                            href={presave.apple_music_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 py-2 rounded-lg bg-[#FA243C]/20 text-[#FA243C] text-sm font-medium flex items-center justify-center gap-1 hover:bg-[#FA243C]/30"
                          >
                            Apple <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Signup Modal */}
      {signupModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-md" data-testid="presave-modal">
            <div className="text-center mb-6">
              <img 
                src={signupModal.cover_url} 
                alt={signupModal.title}
                className="w-32 h-32 rounded-xl object-cover mx-auto mb-4"
              />
              <h2 className="text-xl font-bold">Pre-save &quot;{signupModal.title}&quot;</h2>
              <p className="text-gray-400 text-sm mt-1">Get notified when it drops!</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 outline-none"
                  data-testid="presave-email"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 outline-none"
                  data-testid="presave-name"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setSignupModal(null)}
                className="flex-1 py-3 rounded-lg bg-white/10 hover:bg-white/20"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleSignup(signupModal.id)}
                disabled={submitting || !email}
                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-presave"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" /> Confirm Pre-save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
